<script setup lang="ts">
import {
	ref,
	computed,
	watch,
	onMounted,
	onBeforeMount,
	onBeforeUnmount,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useApiClient } from "@/api/client";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import { toastApiResult } from "@/toaster";
import { useBaseStore } from "@/stores/base";
import { useCampaignPlannerStore } from "../../../store";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import FlightRefineMap from "./FlightRefineMap.vue";
import {
	productContacts as productContactsFor,
	productCpm,
	productMaxContacts,
	productRevenue as productRevenueFor,
} from "./priceQuoteMetrics";
import { availabilityColor } from "./availabilityColor";
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import FlightSegmentDO = Modules.CampaignPlanner.Data.FlightSegmentDO;
import responseProductDTO = Modules.CampaignPlanner.Data.PriceQuoteApi.responseProductDTO;
import SaveSegmentSelectionsDO = Modules.CampaignPlanner.Data.SaveSegmentSelectionsDO;

const isFullscreen = ref(true);

onBeforeMount(() => (useBaseStore().appFullWidth = true));
onBeforeUnmount(() => (useBaseStore().appFullWidth = false));

const router = useRouter();
const store = useCampaignPlannerStore();
const flight_uuid: string = (useRoute().params.flight_uuid ?? "").toString();

const flight = ref<FlightDO | null>(null);
const loading = ref(true);
const activeStep = ref(1);

// Mutable local copy of products per segment uuid
const segmentProducts = ref<Record<string, responseProductDTO[]>>({});

const formatShortDate = (d: string) => dayjs(d).locale("nl").format("D MMM");

const formatDateRange = (start: string, end: string) =>
	start === end
		? formatShortDate(start)
		: `${formatShortDate(start)} — ${formatShortDate(end)}`;

const formatCurrency = (val: number) =>
	new Intl.NumberFormat("nl-NL", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(val);

const formatNumber = (val: number) =>
	new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 0 }).format(val);

const optimizationColor = (result: string | null | undefined): string => {
	switch (result) {
		case "failure":
			return "error";
		case "optimal":
			return "success";
		case "feasible":
			return "info";
		default:
			return "primary";
	}
};

const segments = computed<FlightSegmentDO[]>(
	() => (flight.value?.segments ?? []) as FlightSegmentDO[]
);

const initSegmentProducts = () => {
	const result: Record<string, responseProductDTO[]> = {};
	for (const seg of segments.value) {
		const raw =
			(seg.user_customized_pqa_response as responseProductDTO[] | null) ?? [];
		result[seg.uuid] = JSON.parse(JSON.stringify(raw));
	}
	segmentProducts.value = result;
};

const productContacts = (
	product: responseProductDTO,
	segmentAudienceTotalImpressions: number
): number =>
	productContactsFor(flight.value, product, segmentAudienceTotalImpressions);

const productRevenue = (
	product: responseProductDTO,
	segmentAudienceTotalImpressions: number
): number =>
	productRevenueFor(flight.value, product, segmentAudienceTotalImpressions);

const totalKpis = computed(() => {
	const allReach = Object.values(kpis.value).reduce((s, k) => s + k.reach, 0);
	const allPrice = Object.values(kpis.value).reduce((s, k) => s + k.price, 0);

	console.log(kpis.value, "All Reach: " + allReach, "All Price: " + allPrice);

	const allMaxAvailableImpressions = Object.values(kpis.value).reduce(
		(s, k) => s + k.maxAvailableImpressions,
		0
	);
	const allAudienceTotalImpressions = Object.values(kpis.value).reduce(
		(s, k) => s + k.audienceTotalImpressions,
		0
	);
	return {
		reach: allReach,
		price: allPrice,
		// Blended cost per mille across all segments — total price / total reach.
		cpm: allReach > 0 ? (allPrice / allReach) * 1000 : 0,
		maxAvailableImpressions: allMaxAvailableImpressions,
		audienceTotalImpressions: allAudienceTotalImpressions,
	};
});

// Budget-goal flights track progress against the price total; otherwise against
// the contacts total. Capped at 100% for the bar (overdelivery still shows full).
const isBudgetGoal = computed(() => flight.value?.goal_name_clean === "budget");

// Real percentage (can exceed 100% on overdelivery) — used for the label.
const goalProgress = computed(() => {
	const target = flight.value?.goal_target ?? 0;
	if (target <= 0) return 0;
	const actual = isBudgetGoal.value
		? totalKpis.value.price
		: totalKpis.value.reach;
	return Math.round((actual / target) * 100);
});

// Bar fill is clamped to 100% even when the goal is exceeded.
const goalProgressBar = computed(() => Math.min(goalProgress.value, 100));

// Max available impressions across selected products only, deduplicated by
// product (a product selected in multiple segments is counted once).
const selectedMaxAvailableImpressions = computed(() => {
	const seen = new Map<string, number>();
	for (const products of Object.values(segmentProducts.value)) {
		for (const p of products) {
			if (!p.isSelected) continue;
			const key = p.reference_id ?? p.name ?? "";
			if (seen.has(key)) continue;
			seen.set(key, productMaxContacts(p));
		}
	}
	return Array.from(seen.values()).reduce((sum, v) => sum + v, 0);
});

// Total audience impressions across selected products only, deduplicated by
// product (a product selected in multiple segments is counted once).
const selectedAudienceTotal = computed(() => {
	const seen = new Map<string, number>();
	for (const products of Object.values(segmentProducts.value)) {
		for (const p of products) {
			if (!p.isSelected) continue;
			const key = p.reference_id ?? p.name ?? "";
			if (seen.has(key)) continue;
			seen.set(key, p.used_availability?.audience_total_impression ?? 0);
		}
	}
	return Array.from(seen.values()).reduce((sum, v) => sum + v, 0);
});

// Share of voice: how much of the total audience impressions the reach represents.
const shareOfVoice = computed(() => {
	const available = selectedAudienceTotal.value;
	if (available <= 0) return 0;
	return Math.round((totalKpis.value.reach / available) * 100 * 10) / 10;
});

// Bar fill clamped to 100%.
const shareOfVoiceBar = computed(() => Math.min(shareOfVoice.value, 100));

// Total contacts as a percentage of the selected products' max available.
const contactsFillProgress = computed(() => {
	const available = selectedMaxAvailableImpressions.value;
	if (available <= 0) return 0;
	return Math.round((totalKpis.value.reach / available) * 100 * 10) / 10;
});

// Bar fill clamped to 100%.
const contactsFillBar = computed(() =>
	Math.min(contactsFillProgress.value, 100)
);

// Per-segment share of voice: segment reach over its selected audience total.
const segmentShareOfVoice = (uuid: string): number => {
	const k = kpis.value[uuid];
	const available = k?.audienceTotalImpressions ?? 0;
	if (available <= 0) return 0;
	return Math.round(((k?.reach ?? 0) / available) * 100 * 10) / 10;
};

// Chips turn from primary to error once the goal is exceeded.
const goalChipColor = computed(() =>
	goalProgress.value > 100 ? "error" : "primary"
);

const kpis = computed(() => {
	const result: Record<
		string,
		{
			reach: number;
			price: number;
			cpm: number;
			maxAvailableImpressions: number;
			audienceTotalImpressions: number;
			selected: number;
			total: number;
		}
	> = {};
	for (const [uuid, products] of Object.entries(segmentProducts.value)) {
		const selected = products.filter((p) => p.isSelected);

		const maxAvailableImpressions = selected.reduce(
			(sum, p) => sum + productMaxContacts(p),
			0
		);

		const audienceTotalImpressions = selected.reduce(
			(sum, p) => sum + (p.used_availability?.audience_total_impression ?? 0),
			0
		);

		const reach = selected.reduce(
			(sum, p) => sum + productContacts(p, maxAvailableImpressions),
			0
		);
		const price = selected.reduce(
			(sum, p) => sum + productRevenue(p, maxAvailableImpressions),
			0
		);
		// CPM is the blended cost per mille — total price over total contacts —
		// so it stays consistent with the (share-weighted) price and reach.
		const cpm = reach > 0 ? (price / reach) * 1000 : 0;
		result[uuid] = {
			reach,
			price,
			cpm,
			maxAvailableImpressions,
			audienceTotalImpressions,
			selected: selected.length,
			total: products.length,
		};
	}
	return result;
});

const sortByState = ref<
	Record<string, { key: string; order?: "asc" | "desc" }[]>
>({});

const FE_COMPUTED_KEYS: readonly string[] = [
	"pqa_audience",
	"max_available",
	"pqa_pricequote_exvat",
	"pqa_cpm_average",
];

const computedSortValue = (
	uuid: string,
	key: string,
	p: responseProductDTO
): number => {
	switch (key) {
		case "pqa_audience":
			return contacts(uuid, p);
		case "max_available":
			return maxAvailableImpressions(p);
		case "pqa_pricequote_exvat":
			return revenue(uuid, p);
		case "pqa_cpm_average":
			return cpm(uuid, p);
		default:
			return 0;
	}
};

// Tells Vuetify's data table not to sort these columns itself — see
// `feComputedSort` below. Vuetify's sort is stable, so the order we hand it
// via `:items` is preserved when its own comparator is a no-op.
const neutralSort = () => 0;
const feComputedSort = {
	pqa_audience: neutralSort,
	max_available: neutralSort,
	pqa_pricequote_exvat: neutralSort,
	pqa_cpm_average: neutralSort,
};

const sortedSegmentProducts = computed(() => {
	const result: Record<string, responseProductDTO[]> = {};
	for (const [uuid, products] of Object.entries(segmentProducts.value)) {
		const active = sortByState.value[uuid]?.[0];
		const ordered = [...products];

		if (active && FE_COMPUTED_KEYS.includes(active.key)) {
			const dir = active.order === "desc" ? -1 : 1;
			ordered.sort(
				(a, b) =>
					dir *
					(computedSortValue(uuid, active.key, a) -
						computedSortValue(uuid, active.key, b))
			);
		} else {
			ordered.sort((a, b) => (b.isSelected ? 1 : 0) - (a.isSelected ? 1 : 0));
		}

		result[uuid] = ordered;
	}
	return result;
});

const activeProducts = computed(() => {
	const seg = segments.value[activeStep.value - 1];
	return seg ? segmentProducts.value[seg.uuid] ?? [] : [];
});

const summaryProducts = computed(() => {
	const selectedMap = new Map<string, responseProductDTO>();

	for (const seg of segments.value) {
		const segMaxAvailableImpressions =
			kpis.value[seg.uuid]?.maxAvailableImpressions ?? 0;
		for (const p of segmentProducts.value[seg.uuid] ?? []) {
			if (!p.isSelected) continue;
			const key = p.reference_id ?? p.name ?? "";
			const contactValue = productContacts(p, segMaxAvailableImpressions);
			const existing = selectedMap.get(key);
			if (existing) {
				selectedMap.set(key, {
					...existing,
					pqa_audience: (existing.pqa_audience ?? 0) + contactValue,
				});
			} else {
				selectedMap.set(key, {
					...p,
					isSelected: true,
					pqa_audience: contactValue,
				});
			}
		}
	}

	// Deselected: screens never selected in any segment
	const deselectedMap = new Map<string, responseProductDTO>();
	for (const seg of segments.value) {
		for (const p of segmentProducts.value[seg.uuid] ?? []) {
			const key = p.reference_id ?? p.name ?? "";
			if (!selectedMap.has(key) && !deselectedMap.has(key)) {
				deselectedMap.set(key, { ...p, isSelected: false });
			}
		}
	}

	return [
		...Array.from(selectedMap.values()),
		...Array.from(deselectedMap.values()),
	];
});

const mapProducts = computed(() =>
	activeStep.value === segments.value.length + 1
		? summaryProducts.value
		: activeProducts.value
);

const activeSegUuid = computed(
	() => segments.value[activeStep.value - 1]?.uuid ?? ""
);

const scrollRef = ref();

watch(activeStep, () => {
	if (scrollRef.value?.$el) scrollRef.value.$el.scrollTop = 0;
});

type TableHeader = {
	title: string;
	key: string;
	sortable: boolean;
	align?: "start" | "end" | "center";
	width?: string;
};

const tableHeaders = computed<TableHeader[]>(() => [
	{ title: "", key: "isSelected", width: "52px", sortable: false },
	{ title: "Location", key: "name", sortable: true },
	{
		title: "Contacts",
		key: "pqa_audience",
		align: "end",
		sortable: true,
		width: "110px",
	},
	...(flight.value?.type_of_buy === "goal_impressions"
		? [
				{
					title: "Max. available",
					key: "max_available",
					align: "end" as const,
					sortable: true,
					width: "300px",
				},
		  ]
		: []),
	{
		title: "Price",
		key: "pqa_pricequote_exvat",
		align: "end",
		sortable: true,
		width: "120px",
	},
	{
		title: "CPM",
		key: "pqa_cpm_average",
		align: "end",
		sortable: true,
		width: "100px",
	},
]);

const toggleProduct = (segUuid: string, product: responseProductDTO) => {
	product.isSelected = !product.isSelected;
};

const rowProps =
	(_segUuid: string) => (item: { item: responseProductDTO }) => ({
		class: item.item.isSelected ? "" : "row-deselected",
		style: "cursor: pointer",
	});

const saving = ref(false);

const saveAndClose = async () => {
	if (!flight.value) return;
	saving.value = true;
	const payload: SaveSegmentSelectionsDO = {
		segments: segments.value.map((seg) => ({
			uuid: seg.uuid,
			products: segmentProducts.value[seg.uuid] ?? [],
		})),
	};
	const result = await toastApiResult(
		useApiClient("campaignPlanner").saveSegmentSelections(
			flight.value.uuid,
			payload
		)
	);
	saving.value = false;
	if (result.isOk()) {
		router.push(
			`/campaign-planner/opportunities/detail/${flight.value.opportunity_uuid}`
		);
	}
};

onMounted(async () => {
	const flightRes = await useApiClient("campaignPlanner").getFlight(
		flight_uuid
	);
	if (flightRes.isOk()) {
		flight.value = flightRes.value;
		const f = flightRes.value;

		const oppName = f.opportunity?.name ?? "Opportunity";

		store.setBreadcrumbs([
			{
				title: "Opportunities",
				disabled: false,
				to: "/campaign-planner/opportunities/listing",
			},
			{
				title: oppName,
				disabled: false,
				to: `/campaign-planner/opportunities/detail/${f.opportunity_uuid}`,
			},
			{ title: f.name ?? "Flight", disabled: true, to: "" },
		]);

		initSegmentProducts();
	}
	loading.value = false;
});

const maxAvailableImpressions = (product: responseProductDTO): number =>
	productMaxContacts(product);

const contacts = (segment_uuid: string, product: responseProductDTO): number =>
	productContacts(
		product,
		kpis.value[segment_uuid]?.maxAvailableImpressions ?? 0
	);

const revenue = (segment_uuid: string, product: responseProductDTO): number =>
	productRevenue(
		product,
		kpis.value[segment_uuid]?.maxAvailableImpressions ?? 0
	);

// Same value for both buy types — see `productCpm`. Kept as a function (rather
// than reading `item.pqa_cpm_average` inline) so the template never needs to
// branch on `type_of_buy` and every caller (display, sorting) goes through one
// place.
const cpm = (_segment_uuid: string, product: responseProductDTO): number =>
	productCpm(product);
</script>

<template>
	<Teleport to="body" :disabled="!isFullscreen">
		<div :class="isFullscreen ? 'flight-fullscreen' : 'flight-inapp'">
			<div v-if="loading" class="d-flex align-center justify-center h-100">
				<v-progress-circular indeterminate color="primary" />
			</div>

			<template v-else-if="flight">
				<!-- Toolbar -->
				<v-toolbar v-if="isFullscreen" flat height="60">
					<div class="pl-5">
						<main-breadcrumbs :breadcrumbs="store.breadcrumbs" inline />
					</div>
					<v-spacer />
					<v-tooltip text="Collapse to in-app view" location="bottom">
						<template v-slot:activator="{ props }">
							<v-btn
								icon="fa: fa-regular fa-compress"
								v-bind="props"
								@click="isFullscreen = false"
							/>
						</template>
					</v-tooltip>
				</v-toolbar>
				<v-divider />

				<!-- Two-panel layout -->
				<div class="flight-layout">
					<!-- Left: stepper -->
					<div class="flight-form-col">
						<perfect-scrollbar
							ref="scrollRef"
							:options="{ suppressScrollX: true }"
							class="h-100"
						>
							<div class="pb-5 px-0">
								<div class="d-flex">
									<div class="stat-row-cell pa-4 text-center flex-1-1">
										<div class="text-caption text-medium-emphasis mb-1">
											Total Contacts
										</div>
										<div class="text-h6 font-weight-bold">
											{{ formatNumber(totalKpis.reach) }}
										</div>
										<div
											v-if="flight.type_of_buy === 'goal_impressions'"
											class="goal-progress mt-2"
										>
											<v-progress-linear
												v-tooltip="`hoi`"
												:model-value="contactsFillBar"
												:color="availabilityColor(contactsFillBar)"
												rounded
												height="6"
											/>
											<div
												class="d-flex justify-space-between align-center mt-2"
											>
												<v-chip size="x-small" variant="tonal" color="primary">
													{{ contactsFillProgress }}%
												</v-chip>
												<v-chip size="x-small" variant="tonal" color="primary">
													of {{ formatNumber(selectedMaxAvailableImpressions) }}
												</v-chip>
											</div>
										</div>
									</div>
									<v-divider vertical />
									<div class="stat-row-cell pa-4 text-center flex-1-1">
										<div class="text-caption text-medium-emphasis mb-1">
											Total Price
										</div>
										<div class="text-h6 font-weight-bold">
											{{ formatCurrency(totalKpis.price) }}
										</div>
										<div v-if="isBudgetGoal" class="goal-progress mt-2">
											<v-progress-linear
												:model-value="goalProgressBar"
												:color="availabilityColor(goalProgressBar)"
												rounded
												height="6"
											/>
											<div
												class="d-flex justify-space-between align-center mt-2"
											>
												<v-chip
													size="x-small"
													variant="tonal"
													:color="goalChipColor"
												>
													{{ goalProgress }}%
												</v-chip>
												<v-chip
													size="x-small"
													variant="tonal"
													:color="goalChipColor"
												>
													of {{ formatCurrency(flight.goal_target) }}
												</v-chip>
											</div>
										</div>
									</div>
									<v-divider vertical />
									<div class="stat-row-cell pa-4 text-center flex-1-1">
										<div class="text-caption text-medium-emphasis mb-1">
											Average CPM
										</div>
										<div class="text-h6 font-weight-bold">
											{{ formatCurrency(totalKpis.cpm) }}
										</div>
									</div>
									<template v-if="flight.type_of_buy === 'goal_impressions'">
										<v-divider vertical />
										<div class="stat-row-cell pa-4 text-center flex-1-1">
											<div class="text-caption text-medium-emphasis mb-1">
												Share of Voice
											</div>
											<div class="text-h6 font-weight-bold">
												{{ shareOfVoice }}%
											</div>
											<v-tooltip activator="parent">
												{{ formatNumber(totalKpis.reach) }} of audience total
												{{ formatNumber(selectedAudienceTotal) }}
											</v-tooltip>
										</div>
									</template>
								</div>
								<v-divider />

								<!-- Segment stepper -->
								<v-stepper-vertical
									v-model="activeStep"
									flat
									bg-color="transparent"
								>
									<v-stepper-vertical-item
										v-for="(seg, i) in segments"
										:key="seg.uuid"
										:value="i + 1"
										:color="activeStep === i + 1 ? 'primary' : 'grey'"
										style="box-shadow: unset !important"
										editable
									>
										<template v-slot:title>
											<span class="text-body-2 font-weight-medium">
												Segment {{ i + 1 }}:
												{{ formatDateRange(seg.start_date, seg.end_date) }}
											</span>
											<v-chip
												size="x-small"
												variant="tonal"
												color="primary"
												class="ml-3"
											>
												{{ kpis[seg.uuid]?.selected ?? 0 }} /
												{{ kpis[seg.uuid]?.total ?? 0 }} selected
											</v-chip>
											<div
												v-if="seg.pqa_optimization_result"
												style="position: absolute; right: 10px; top: 20px"
											>
												<v-chip
													size="x-small"
													variant="tonal"
													:color="
														optimizationColor(seg.pqa_optimization_result)
													"
													class="mr-3"
													>Optimization:
													<div style="font-weight: bolder" class="pl-2">
														{{ seg.pqa_optimization_result }}
													</div></v-chip
												>
											</div>
										</template>

										<!-- KPI blocks -->
										<div class="kpi-row mb-4">
											<div class="kpi-block">
												<div class="text-caption text-medium-emphasis mb-1">
													Contacts
												</div>
												<div class="text-h6 font-weight-bold">
													{{ formatNumber(kpis[seg.uuid]?.reach ?? 0) }}
												</div>
											</div>
											<div class="kpi-block">
												<div class="text-caption text-medium-emphasis mb-1">
													Price
												</div>
												<div class="text-h6 font-weight-bold">
													{{ formatCurrency(kpis[seg.uuid]?.price ?? 0) }}
												</div>
											</div>
											<div class="kpi-block">
												<div class="text-caption text-medium-emphasis mb-1">
													CPM
												</div>
												<div class="text-h6 font-weight-bold">
													{{ formatCurrency(kpis[seg.uuid]?.cpm ?? 0) }}
												</div>
											</div>
											<div
												v-if="flight.type_of_buy === 'goal_impressions'"
												class="kpi-block"
											>
												<div class="text-caption text-medium-emphasis mb-1">
													Share of Voice
												</div>
												<div class="text-h6 font-weight-bold">
													{{ segmentShareOfVoice(seg.uuid) }}%
												</div>
											</div>
										</div>
										<!-- Product table -->
										<v-data-table
											v-if="sortedSegmentProducts[seg.uuid]"
											:headers="tableHeaders"
											:items="sortedSegmentProducts[seg.uuid]"
											:sort-by="sortByState[seg.uuid] ?? []"
											@update:sort-by="
												(v: { key: string; order?: 'asc' | 'desc' }[]) =>
													(sortByState[seg.uuid] = v)
											"
											:custom-key-sort="feComputedSort"
											:items-per-page="50"
											item-value="reference_id"
											density="compact"
											class="product-table"
											:row-props="rowProps(seg.uuid)"
											@click:row="
												(_: Event, row: { item: responseProductDTO }) =>
													toggleProduct(seg.uuid, row.item)
											"
										>
											<template v-slot:item.isSelected="{ item }">
												<v-checkbox-btn
													:model-value="item.isSelected"
													color="primary"
													density="compact"
													@click.stop="toggleProduct(seg.uuid, item)"
												/>
											</template>

											<template v-slot:item.name="{ item }">
												<v-tooltip
													:text="`${item.reference_id} - ${item.name}`"
													location="top"
													:disabled="!item.reference_id"
												>
													<template v-slot:activator="{ props: ttProps }">
														<span v-bind="ttProps" class="screen-name">
															{{ item.name ?? "—" }}
														</span>
													</template>
												</v-tooltip>
											</template>

											<template v-slot:item.pqa_audience="{ item }">
												<span class="text-body-2">
													{{ formatNumber(contacts(seg.uuid, item)) }}
												</span>
											</template>

											<template v-slot:item.max_available="{ item }">
												<span class="text-body-2">
													{{ formatNumber(maxAvailableImpressions(item)) }}
												</span>
											</template>

											<template v-slot:item.pqa_pricequote_exvat="{ item }">
												<span class="text-body-2">
													{{ formatCurrency(revenue(seg.uuid, item)) }}
												</span>
											</template>

											<template v-slot:item.pqa_cpm_average="{ item }">
												<span class="text-body-2">
													{{ formatCurrency(cpm(seg.uuid, item)) }}
												</span>
											</template>

											<template
												v-if="
													(sortedSegmentProducts[seg.uuid]?.length ?? 0) <= 50
												"
												v-slot:bottom
											/>
										</v-data-table>

										<template v-slot:next="{ next }">
											<v-btn
												v-if="i < segments.length - 1"
												color="primary"
												@click="next"
											>
												Next segment
											</v-btn>
											<v-btn
												v-else
												color="primary"
												@click="activeStep = segments.length + 1"
											>
												Review &amp; Save
											</v-btn>
										</template>
										<template v-slot:prev="{ prev }">
											<v-btn v-if="i > 0" variant="text" @click="prev">
												Previous
											</v-btn>
										</template>
									</v-stepper-vertical-item>

									<!-- Summary step — always last -->
									<v-stepper-vertical-item
										:value="segments.length + 1"
										:color="
											activeStep === segments.length + 1 ? 'primary' : 'grey'
										"
										editable
										style="box-shadow: unset !important"
									>
										<template v-slot:title>
											<span class="text-body-2 font-weight-medium"
												>Summary</span
											>
										</template>

										<div
											v-if="segments.length === 0"
											class="d-flex flex-column align-center justify-center py-10 text-medium-emphasis"
										>
											<v-icon size="40" class="mb-3" opacity="0.3"
												>fa: fa-regular fa-calendar-xmark</v-icon
											>
											<div class="text-body-2">
												No segments found for this flight.
											</div>
										</div>

										<template v-else>
											<v-table density="compact" class="summary-table mb-4">
												<thead>
													<tr>
														<th class="text-left">Period</th>
														<th class="text-right">Selected/Available</th>
														<th class="text-right">Reach</th>
														<th class="text-right">Price</th>
														<th class="text-right">CPM</th>
														<th
															v-if="flight.type_of_buy === 'goal_impressions'"
															class="text-right"
														>
															Share of Voice
														</th>
													</tr>
												</thead>
												<tbody>
													<tr v-for="seg in segments" :key="seg.uuid">
														<td class="text-body-2">
															{{
																formatDateRange(seg.start_date, seg.end_date)
															}}
														</td>
														<td class="text-body-2 text-right">
															{{ kpis[seg.uuid]?.selected ?? 0 }} /
															{{ kpis[seg.uuid]?.total ?? 0 }}
														</td>
														<td class="text-body-2 text-right">
															{{ formatNumber(kpis[seg.uuid]?.reach ?? 0) }}
														</td>
														<td class="text-body-2 text-right">
															{{ formatCurrency(kpis[seg.uuid]?.price ?? 0) }}
														</td>
														<td class="text-body-2 text-right">
															{{ formatCurrency(kpis[seg.uuid]?.cpm ?? 0) }}
														</td>
														<td
															v-if="flight.type_of_buy === 'goal_impressions'"
															class="text-body-2 text-right"
														>
															{{ segmentShareOfVoice(seg.uuid) }}%
														</td>
													</tr>
												</tbody>
												<tfoot>
													<tr class="summary-total-row">
														<td class="text-body-2 font-weight-bold">Total</td>
														<td></td>
														<td class="text-body-2 font-weight-bold text-right">
															{{ formatNumber(totalKpis.reach) }}
														</td>
														<td class="text-body-2 font-weight-bold text-right">
															{{ formatCurrency(totalKpis.price) }}
														</td>
														<td class="text-body-2 font-weight-bold text-right">
															{{ formatCurrency(totalKpis.cpm) }}
														</td>
														<td
															v-if="flight.type_of_buy === 'goal_impressions'"
															class="text-body-2 font-weight-bold text-right"
														>
															{{ shareOfVoice }}%
														</td>
													</tr>
												</tfoot>
											</v-table>

											<div class="d-flex justify-end">
												<v-btn
													color="primary"
													:loading="saving"
													@click="saveAndClose"
												>
													Save and close
												</v-btn>
											</div>
										</template>

										<template v-slot:prev>
											<v-btn
												v-if="segments.length > 0"
												variant="text"
												@click="activeStep = segments.length"
											>
												Previous
											</v-btn>
										</template>
										<template v-slot:next />
									</v-stepper-vertical-item>
								</v-stepper-vertical>
							</div>
						</perfect-scrollbar>
					</div>

					<!-- Right: heatmap -->
					<div class="flight-map-col">
						<flight-refine-map
							:flight="flight"
							:products="mapProducts"
							:readonly="activeStep === segments.length + 1"
							@toggle-product="(p) => toggleProduct(activeSegUuid, p)"
						/>
						<v-tooltip
							v-if="!isFullscreen"
							text="Expand to full screen"
							location="bottom"
						>
							<template v-slot:activator="{ props }">
								<v-btn
									icon="fa: fa-regular fa-expand"
									variant="elevated"
									size="small"
									v-bind="props"
									class="map-fullscreen-btn"
									@click="isFullscreen = true"
								/>
							</template>
						</v-tooltip>
					</div>
				</div>
			</template>
		</div>
	</Teleport>
</template>

<style scoped lang="scss">
.flight-fullscreen {
	position: fixed;
	inset: 0;
	z-index: 1999;
	background: rgb(var(--v-theme-background));
	display: flex;
	flex-direction: column;
}

.flight-inapp {
	display: flex;
	flex-direction: column;
	height: calc(100dvh - var(--v-layout-top) - 56px);
	overflow: hidden;
}

.flight-layout {
	display: flex;
	flex: 1;
	overflow: hidden;
	min-height: 0;
}

.flight-form-col {
	flex: 0 0 50%;
	height: 100%;
	border-right: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.flight-map-col {
	flex: 1;
	height: 100%;
	position: relative;
}

.map-fullscreen-btn {
	position: absolute;
	top: 12px;
	right: 12px;
	z-index: 1;
}

.screen-name {
	display: inline-block;
	max-width: 25ch;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.stat-row-cell {
	flex: 1 1 0;
}

.kpi-row {
	display: grid;
	grid-auto-flow: column;
	grid-auto-columns: minmax(0, 1fr);
	gap: 12px;
}

.kpi-block {
	padding: 12px 16px;
	background: rgba(var(--v-theme-on-surface), 0.04);
	border-radius: 8px;
}

.product-table {
	:deep(.row-deselected) {
		opacity: 0.45;
	}

	:deep(tr) {
		cursor: pointer;
	}
}

.summary-table {
	border-radius: 8px;
	overflow: hidden;

	:deep(th) {
		font-size: 0.75rem;
		opacity: 0.65;
		white-space: nowrap;
	}
}

.summary-total-row {
	td {
		border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	}
}
</style>
